import { css, styled, keyframes } from 'styled-components';
import { FontSizes } from '../../../common/typography/typography.styles';
import { TagContainer } from '../../../common/tag/tag.styles';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';

const bounceAnimation = keyframes`
  0% {
    top: calc(-100% + 16px);
    right: 0;
  }
  90% {
    transform: translateX(48%);
    right: 50%;
  }
  100% {
    top: calc(100% - 17px);
    right: 50%;
    transform: translateX(50%);
  }
`;

const growWidth = (numOfCards: number) => keyframes`
  0% {
    width: 100%
  }
  80% {
    width: calc(92% / ${numOfCards} - 1.6rem + 1.6rem / ${numOfCards});
  }
  100% {
    width: calc(100% / ${numOfCards} - 1.6rem + 1.6rem / ${numOfCards});
  }
`;

const shrinkWidth = (numOfCards: number) => keyframes`
  0% {
    width: calc(100% / ${numOfCards} - 1.6rem + 1.6rem / ${numOfCards});
  }
  100% {
    width: 100%;
  }
`;

export const MiddleMileCardTopPart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: 0.5s all ease-in-out;

  @media screen and (max-width: 650px) {
    position: absolute;
    left: 0;
    padding-top: 48px;
    svg {
      position: absolute;
      top: 0;
      left: 0;
      transition: 0.5s all ease;
    }
  }
`;

export const MiddleMileCardBottomPart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export const MiddleMileTitle = styled.span`
  font-size: ${FontSizes.mediumPlus};
  line-height: 2.4rem;
  letter-spacing: 0.5px;
`;

export const PriceRangeText = styled.span`
  font-size: ${FontSizes.normal};
  line-height: 2rem;
  letter-spacing: 0.5px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  transition: 0.3s all ease;
  height: 20px;
  width: max-content;
`;

export const DeliveryMethodTabContentWrapper = styled.div<{
  $isMiddleMileSelected: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.5s ease-in-out;

  svg {
    width: 60px;
    height: 60px;
  }

  @media screen and (max-width: 650px) {
    ${({ $isMiddleMileSelected }) => {
      if ($isMiddleMileSelected) {
        return css`
          gap: 4px;
          height: 100%;
          position: relative;
          svg {
            width: 22px;
            height: 22px;
            transition: all 0.5s ease-in-out;
          }
          ${MiddleMileCardTopPart} {
            gap: 4px;
            left: 50%;
            transform: translatex(-50%);
            padding-top: 26px;
            svg {
              left: 50%;
              transform: translatex(-50%);
            }
            ${MiddleMileTitle} {
              font-size: ${FontSizes.normal};
              transition: all 0.5s ease-in-out;
            }
          }
          ${MiddleMileCardBottomPart} {
            gap: 0;
            transition: 0.5s all ease;
            position: absolute;
            animation: ${bounceAnimation} 0.5s ease forwards;
            width: max-content;
            max-width: 100%;
            ${TagContainer} {
              background-color: inherit;
              padding-top: 0;
              padding-bottom: 0;
              transition: all 0.5s ease;
              width: max-content;
            }
            ${PriceRangeText} {
              opacity: 0;
            }
          }
        `;
      }
      return css`
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        transition: 0.5s all ease;
        height: 100%;
        svg {
          transition: 0.5s all ease;
          width: 40px;
          height: 40px;
        }
        ${MiddleMileCardTopPart} {
          align-items: start;
          gap: 5px;
        }
        ${MiddleMileCardBottomPart} {
          gap: 25px;
          position: absolute;
          top: 0;
          right: 0;
          transition: 0.5s all ease-in-out;
          padding-top: 48px;
          ${TagContainer} {
            position: absolute;
            top: 0;
            right: 0;
            transition: 0.5s all ease-in-out;
            width: max-content;
          }
          ${PriceRangeText} {
            color: ${({ theme }) => theme.colors.baseColors.grey150};
          }
        }
      `;
    }}
  }
`;

export const DeliveryMethodTab = styled.div<{
  $active: boolean;
  $isMiddleMileSelected: boolean;
}>`
  padding: 2rem;
  border: 1.5px solid;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.5s ease-in-out;

  border-color: ${({ $active, theme }) =>
    $active
      ? theme.colors.namedColors.primarySelected
      : theme.colors.baseColors.grey50};
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.baseColors.blue0 : '#fff'};

  ${({ $active }) => {
    if (!$active) return;

    return css`
      transform: scaleX(1.02);
      transform: scaleY(1.03);
      ${DeliveryMethodTabContentWrapper} {
        transform: scaleX(0.98);
        transform: scaleY(0.97);
      }
    `;
  }}

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    ${({ $active }) => {
      if ($active) return;

      return css`
        border-color: ${({ theme }) => theme.colors.baseColors.grey50};
        background-color: ${({ theme }) => theme.colors.baseColors.grey5};
      `;
    }}
  }

  @media screen and (max-width: 650px) {
    height: 103px;
    position: absolute;
    left: 0;
    top: 0;
    transition: all 0.5s ease-in-out;
    &:first-child {
      transition-delay: 0.2s !important;
    }
    &:nth-child(2) {
      top: 115px;
      transition-delay: 0.05s !important;
      right: 0;
    }
    &:nth-child(3) {
      top: 230px;
      left: unset;
      right: 0;
    }
    ${({ $isMiddleMileSelected }) => {
      if ($isMiddleMileSelected) {
        return css`
          padding: 8px 9px;
          height: 93px;
          &:nth-child(2) {
            left: 50%;
            transform: translateX(-50%);
            top: 1px;
          }
          &:nth-child(3) {
            top: 1px;
          }
        `;
      }
      return css`
        padding: 1.6rem 2rem;
      `;
    }}
  }
`;

interface MiddleMileFilterHeaderProps {
  $isMiddleMileSelected?: boolean;
}

export const MiddleMileFilterHeader = styled(
  FlexContainer
)<MiddleMileFilterHeaderProps>`
  transition: all 0.6s ease;
  max-height: 100%;

  ${({ $isMiddleMileSelected }) =>
    $isMiddleMileSelected &&
    css`
      @media screen and (max-width: 650px) {
        opacity: 0;
        margin: -4px;
      }
    `}
`;

interface MiddleMileFiltersContainerProps {
  $numOfCards: number;
  $isMiddleMileSelected?: boolean;
  $isAnimationTriggered?: boolean | undefined;
}

export const MiddleMileFiltersContainer = styled.div<MiddleMileFiltersContainerProps>`
  display: flex;
  gap: 1.6rem;
  padding: 5px 0;
  position: relative;
  transition: 0.5s all ease-in-out;
  flex-wrap: wrap;
  justify-content: flex-start;

  ${({ $numOfCards }) => {
    const numOfCards = $numOfCards === 1 ? 2 : $numOfCards;
    return $numOfCards < 4
      ? css`
          ${DeliveryMethodTab} {
            @media screen and (min-width: 650px) {
              flex: 1 1
                calc(100% / ${numOfCards} - 1.6rem + 1.6rem / ${numOfCards});
              max-width: calc(
                100% / ${numOfCards} - 1.6rem + 1.6rem / ${numOfCards}
              );
            }
          }
        `
      : css`
          ${DeliveryMethodTab} {
            @media screen and (min-width: 650px) {
              flex: 1 1 calc(50% - 1.6rem + 1.6rem / 2);
              max-width: calc(50% - 1.6rem + 1.6rem / 2);
            }
          }
        `;
  }}

  @media screen and (max-width: 650px) {
    ${({ $isMiddleMileSelected, $numOfCards, $isAnimationTriggered }) => {
      const numOfCards = $numOfCards === 1 ? 2 : $numOfCards;
      if (!$isMiddleMileSelected) {
        return css`
          height: calc(103px * ${numOfCards} + 12px * ${numOfCards - 1});

          ${DeliveryMethodTab} {
            width: 100%;
            flex: none;
            transform: translateX(0);
            transition: all 0.5s ease-in-out;
            ${$isAnimationTriggered &&
            css`
              animation: ${shrinkWidth(numOfCards)} 0.5s ease-out forwards;
            `}
          }
        `;
      }
      return css`
        height: 96px;

        ${DeliveryMethodTab} {
          animation: ${growWidth(numOfCards)} 0.5s ease-out forwards;
          flex: 0 0 auto;
          opacity: 1;
          transition: all 0.4s ease-in-out;
          ${$numOfCards === 2 &&
          css`
            &:nth-child(2) {
              transform: translateX(0%);
            }
          `}
        }
      `;
    }}
  }
`;

export const StarCommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 13px;
  font-weight: 400;
  font-size: ${FontSizes.small};
  line-height: 16px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.baseColors.grey100};
`;

export const TabsNavItem = styled.div`
  position: absolute;
  width: 4.2rem;
  height: 4.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  right: -16px;
  top: 8.3rem;
  background: linear-gradient(
    270deg,
    #f4f7fc 30%,
    rgba(244, 247, 252, 0.1) 100%
  );
  z-index: 10;
  transition: 0.1s all ease;

  &.tab-nav-item-prev {
    transform: rotate(180deg);
    justify-content: center;
    left: -16px;
    right: unset;
  }

  svg {
    display: block;
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;
